import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import api from "@/lib/api";

const NAV = [
  { to: "/", label: "Home" },
  {
    label: "About",
    children: [
      { to: "/about", label: "About Us" },
      { to: "/beliefs", label: "Beliefs" },
    ],
  },
  { to: "/events", label: "Events" },
  { to: "/sermons", label: "Sermons" },
  { to: "/ministries", label: "Ministries" },
  { to: "/sunday-school", label: "Sunday School" },
  { to: "/gallery", label: "Gallery" },
  { to: "/testimonies", label: "Testimonies" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [live, setLive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      api.get("/youtube/live-status")
        .then((r) => { if (!cancelled) setLive(r.data?.is_live ? r.data : null); })
        .catch(() => {});
    };
    tick();
    const t = setInterval(tick, 60_000); // poll every 60s
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <header
      data-testid="navbar"
      className="fixed top-0 inset-x-0 z-50 glass border-b border-white/10"
    >
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 lg:px-6 xl:px-10 h-20 md:h-28 flex items-center justify-between gap-3 xl:gap-6">
        <Link
          to="/"
          data-testid="navbar-logo"
          aria-label="UGYC Dubai · Home"
          className="flex items-center gap-2 lg:gap-3 group shrink-0"
          onClick={() => setOpen(false)}
        >
          <img
            src="/brand/ugyc-logo-white.webp"
            alt=""
            className="h-14 md:h-20 xl:h-24 w-auto object-contain group-hover:scale-105 transition-transform"
          />
            <span className="font-display tracking-tight uppercase whitespace-nowrap leading-[0.85] flex flex-col">
              <span className="text-lg lg:text-xl xl:text-2xl">UGYC</span>
              <span className="text-lg lg:text-xl xl:text-2xl text-[#ff5c00]">Dubai</span>
            </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-3 xl:gap-6">
          {NAV.map((n) =>
            n.children ? (
              <DropdownLink key={n.label} item={n} />
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono-accent text-[11px] xl:text-xs tracking-[0.1em] xl:tracking-[0.18em] uppercase whitespace-nowrap transition-colors ${
                    isActive ? "text-white" : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {n.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-2 xl:gap-4 shrink-0">
          {live?.is_live && (
            <a
              href={live.watch_url}
              target="_blank"
              rel="noreferrer"
              data-testid="live-now-pill"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/40 hover:bg-red-500/25 transition-colors"
              title="Watch live on YouTube"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-red-300 whitespace-nowrap">Live Now</span>
            </a>
          )}
          <Link
            to="/#plan-visit"
            data-testid="navbar-cta"
            className="inline-flex items-center whitespace-nowrap bg-[#ff5c00] hover:bg-[#ff7a33] text-white font-mono-accent text-[11px] xl:text-xs tracking-[0.1em] xl:tracking-[0.18em] uppercase px-3 py-2 xl:px-5 xl:py-2.5 rounded-sm transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgba(255,92,0,0.6)]"
          >
            Plan a Visit
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden text-white p-2"
          aria-label="Toggle navigation"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div
          data-testid="navbar-mobile-menu"
          className="lg:hidden border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl"
        >
          <nav className="flex flex-col px-6 py-4 gap-3">
            {NAV.map((n) =>
              n.children ? (
                <div key={n.label} className="space-y-2">
                  <p className="font-mono-accent text-sm tracking-[0.18em] uppercase text-zinc-500 py-1">{n.label}</p>
                  {n.children.map((c) => (
                    <NavLink
                      key={c.to}
                      to={c.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${c.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `block font-mono-accent text-sm tracking-[0.18em] uppercase py-1 pl-4 border-l border-white/10 ${
                          isActive ? "text-[#ff5c00]" : "text-zinc-300"
                        }`
                      }
                    >
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-mono-accent text-sm tracking-[0.18em] uppercase py-1 ${
                      isActive ? "text-[#ff5c00]" : "text-zinc-300"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              )
            )}
            <Link
              to="/#plan-visit"
              onClick={() => setOpen(false)}
              data-testid="mobile-navbar-cta"
              className="mt-2 inline-flex items-center justify-center bg-[#ff5c00] text-white font-mono-accent text-xs tracking-[0.18em] uppercase px-5 py-3 rounded-sm"
            >
              Plan a Visit
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

function DropdownLink({ item }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const activeChild = item.children.some((c) => c.to === pathname);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-link-${item.label.toLowerCase()}`}
        className={`inline-flex items-center gap-1 font-mono-accent text-[11px] xl:text-xs tracking-[0.1em] xl:tracking-[0.18em] uppercase whitespace-nowrap transition-colors ${
          activeChild ? "text-white" : "text-zinc-400 hover:text-white"
        }`}
      >
        {item.label} <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div
          data-testid={`nav-submenu-${item.label.toLowerCase()}`}
          className="absolute left-0 top-full pt-3 min-w-[180px]"
        >
          <div className="glass border border-white/10 rounded-sm py-2 shadow-2xl">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-5 py-2 font-mono-accent text-xs tracking-[0.18em] uppercase transition-colors ${
                    isActive ? "text-[#ff5c00]" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {c.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
